import { createTheme } from '@mui/material/styles'
import {
    ACCENT,
    BRAND,
    CONTROL,
    FONT,
    ON_ORANGE,
    ON_STRONG,
    ON_SUCCESS,
    RADIUS,
    SHADOW,
    STATUS,
    STEP,
    SURFACE,
    SYSTEM,
    TEXT,
    resolveColor,
} from './theme/tokens'

/**
 * Build the MUI theme for one of the two themes ('light' or 'dark').
 *
 * Every value comes from `theme/tokens.js`, resolved to that theme's concrete
 * colour: MUI computes hovers and alphas from its palette, which therefore
 * cannot hold CSS variables. Pages use the tokens themselves (CSS variables),
 * so the palette and the pages always agree.
 *
 * The shape of the design is the Bhanzu brand system: Almost White page (or
 * the Deep Blue dark), white Frames with a soft shadow, Deep Blue type,
 * Poppins headings over Roboto body copy, pill buttons, and Bhanzu Orange
 * kept for functional elements (buttons, focus, selection). Inputs, selects,
 * menus and tables are styled here, so pages never need per-field overrides
 * (the root cause of v1's black-text-on-dark bug).
 *
 * @param {'light'|'dark'} mode
 */
export const createAppTheme = (mode) => {
    const c = (token) => resolveColor(token, mode)
    const dark = mode === 'dark'
    // A status colour as MUI palette entry: the text-safe foreground, with the
    // label colour its contained chips, badges and buttons need (white on the
    // darkened light-theme hues, Deep Blue on the bright dark-theme ones).
    const tone = (key) => ({
        main: c(STATUS[key].fg),
        contrastText: dark ? BRAND.deepBlue : ON_STRONG,
    })

    return createTheme({
        typography: {
            fontFamily: FONT.body,
            // MUI's own component sizes (chips, inputs) scale from this.
            fontSize: 13,
            // Poppins SemiBold headings at console sizes: the stylesheet's
            // ratios, not its marketing sizes (H1 96px).
            h1: {
                fontFamily: FONT.heading,
                fontSize: '1.5rem',
                fontWeight: 600,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
            },
            h2: {
                fontFamily: FONT.heading,
                fontSize: '1.25rem',
                fontWeight: 600,
                lineHeight: 1.35,
            },
            h3: {
                fontFamily: FONT.heading,
                fontSize: '1.125rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },
            h4: {
                fontFamily: FONT.heading,
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.45,
            },
            h5: {
                fontFamily: FONT.heading,
                fontSize: '0.9375rem',
                fontWeight: 600,
                lineHeight: 1.45,
            },
            h6: {
                fontFamily: FONT.heading,
                fontSize: '0.875rem',
                fontWeight: 600,
                lineHeight: 1.5,
            },
            subtitle1: {
                fontFamily: FONT.heading,
                fontSize: '0.875rem',
                fontWeight: 500,
                lineHeight: 1.5,
            },
            subtitle2: {
                fontFamily: FONT.heading,
                fontSize: '0.8125rem',
                fontWeight: 500,
                lineHeight: 1.5,
            },
            // Roboto body. body2 (13px) is the console default.
            body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55 },
            body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
            caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.45 },
            // The stylesheet's OVERLINE: Roboto Bold, upper case, tracked; for
            // eyebrow labels, table headers and small tags.
            overline: {
                fontSize: '0.6875rem',
                fontWeight: 700,
                lineHeight: 1.6,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
            },
            // Sentence case (long labels in a dense console); Roboto Medium.
            button: {
                fontSize: '0.8125rem',
                fontWeight: 500,
                lineHeight: 1.5,
                letterSpacing: '0.01em',
                textTransform: 'none',
            },
        },
        palette: {
            mode,
            // contrastText feeds every MUI surface painted primary (contained
            // buttons, primary chips and badges): Deep Blue, not white, for AA.
            primary: {
                main: BRAND.orange,
                light: BRAND.orangeTint,
                dark: BRAND.orangeDeep,
                contrastText: ON_ORANGE,
            },
            secondary: { main: c(TEXT.muted), contrastText: c(SURFACE.card) },
            success: tone('success'),
            warning: tone('warning'),
            error: tone('error'),
            info: tone('info'),
            text: {
                primary: c(TEXT.primary),
                secondary: c(TEXT.secondary),
                disabled: c(TEXT.disabled),
            },
            // MUI's defaults are 0.38 black / 0.3 white (about 2.6:1).
            action: {
                hover: c(SURFACE.hover),
                selected: c(ACCENT.bg15),
                disabled: c(TEXT.disabled),
                disabledBackground: c(SURFACE.sunken),
            },
            divider: c(SURFACE.border),
            background: { default: c(SURFACE.page), paper: c(SURFACE.card) },
        },
        shape: { borderRadius: RADIUS.sm },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        color: c(TEXT.primary),
                        backgroundColor: c(SURFACE.page),
                    },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true, size: 'small' },
                styleOverrides: {
                    root: {
                        // Stylesheet buttons are pills.
                        borderRadius: RADIUS.pill,
                        lineHeight: 1.5,
                        // Pin one box for every variant, so contained and
                        // outlined buttons side by side share a height.
                        minHeight: 32,
                        padding: '6px 16px',
                        boxSizing: 'border-box',
                        // A pill whose label wraps turns into an oval (the
                        // Holidays rows' "Mark inactive" did): one line.
                        whiteSpace: 'nowrap',
                    },
                    contained: {
                        '&.MuiButton-containedPrimary': {
                            backgroundColor: BRAND.orange,
                            color: ON_ORANGE,
                            // Lighter on hover, not darker: Deep Blue type
                            // stays AA on the tint (6.0:1), not on a darker
                            // orange (3.6:1). Darker only while pressed.
                            '&:hover': { backgroundColor: BRAND.orangeTint },
                        },
                        // Destructive confirms use the darkened red so white
                        // type stays AA (5.7:1).
                        '&.MuiButton-containedError': {
                            backgroundColor: SYSTEM.errorDeep,
                            color: ON_STRONG,
                            '&:hover': {
                                backgroundColor: SYSTEM.errorDeepHover,
                            },
                        },
                        // Approve-type confirms. Deep Blue type: white on the
                        // success green is 2.2:1.
                        '&.MuiButton-containedSuccess': {
                            backgroundColor: SYSTEM.success,
                            color: ON_SUCCESS,
                            '&:hover': { backgroundColor: SYSTEM.successHover },
                        },
                        '&.Mui-disabled': {
                            backgroundColor: c(SURFACE.sunken),
                            color: c(TEXT.disabled),
                        },
                    },
                    outlined: {
                        padding: '6px 16px',
                        borderColor: c(SURFACE.borderHover),
                        color: c(TEXT.secondary),
                        '&:hover': {
                            backgroundColor: c(ACCENT.bg08),
                            borderColor: BRAND.orange,
                            color: c(TEXT.primary),
                        },
                    },
                    // A text button's LABEL is text, and MUI paints it
                    // primary.main: orange on a white dialog is 3.3:1. Every
                    // dialog's Cancel shipped that way in Labs.
                    textPrimary: {
                        color: c(TEXT.primary),
                        '&:hover': { backgroundColor: c(SURFACE.hover) },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: { '&:hover': { backgroundColor: c(SURFACE.hover) } },
                },
            },
            MuiTextField: { defaultProps: { size: 'small' } },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: RADIUS.sm,
                        color: c(TEXT.primary),
                        backgroundColor: c(SURFACE.inputBg),
                        '& fieldset': { borderColor: c(SURFACE.borderHover) },
                        '&:hover fieldset': { borderColor: c(TEXT.faint) },
                        '&.Mui-focused fieldset': {
                            borderColor: BRAND.orange,
                            borderWidth: 1,
                        },
                    },
                    input: {
                        padding: '7px 12px',
                        color: c(TEXT.primary),
                        '&::placeholder': { color: c(TEXT.faint), opacity: 1 },
                    },
                },
            },
            MuiInputLabel: {
                defaultProps: { size: 'small' },
                styleOverrides: { root: { color: c(TEXT.muted) } },
            },
            // A focused field's LABEL is text: MUI paints it primary.main,
            // orange on white at 3.3:1. The focused outline keeps the orange
            // (graphical, 3:1). An errored field keeps its error colour.
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused:not(.Mui-error)': {
                            color: c(TEXT.primary),
                        },
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: { root: { color: c(TEXT.faint) } },
            },
            MuiSelect: {
                defaultProps: { size: 'small' },
                styleOverrides: {
                    icon: { color: c(TEXT.muted) },
                    select: { padding: '7px 12px', minHeight: 'unset' },
                },
            },
            MuiFormControl: { defaultProps: { size: 'small' } },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        backgroundColor: c(SURFACE.cardRaised),
                        border: `1px solid ${c(SURFACE.border)}`,
                        borderRadius: RADIUS.sm,
                        boxShadow: c(SHADOW.popover),
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        backgroundColor: c(SURFACE.cardRaised),
                        border: `1px solid ${c(SURFACE.border)}`,
                        boxShadow: c(SHADOW.popover),
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    paper: {
                        marginTop: 4,
                        backgroundColor: c(SURFACE.cardRaised),
                        border: `1px solid ${c(SURFACE.border)}`,
                        borderRadius: RADIUS.sm,
                        boxShadow: c(SHADOW.popover),
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        color: c(TEXT.secondary),
                        minHeight: 32,
                        padding: '5px 12px',
                        '&:hover': { backgroundColor: c(SURFACE.hover) },
                        '&.Mui-selected': {
                            backgroundColor: c(ACCENT.bg15),
                            color: c(TEXT.primary),
                            fontWeight: 500,
                            '&:hover': { backgroundColor: c(ACCENT.bg22) },
                        },
                    },
                },
            },
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: { backgroundImage: 'none' },
                    // The brand's Frames: every outlined Paper is a card.
                    outlined: {
                        borderColor: c(SURFACE.border),
                        borderRadius: RADIUS.lg,
                        boxShadow: c(SHADOW.card),
                    },
                },
            },
            MuiChip: {
                defaultProps: { size: 'small' },
                styleOverrides: {
                    root: {
                        fontWeight: 500,
                        borderRadius: RADIUS.pill,
                        height: 22,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        minHeight: 36,
                        padding: '6px 12px',
                        color: c(TEXT.muted),
                        '&.Mui-selected': {
                            color: c(TEXT.primary),
                            fontWeight: 600,
                        },
                    },
                    // MUI paints the selected tab's label primary.main.
                    textColorPrimary: {
                        '&.Mui-selected': { color: c(TEXT.primary) },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: { backgroundColor: BRAND.orange, height: 2 },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: '0.75rem',
                        color: c(TEXT.primary),
                        backgroundColor: c(SURFACE.cardRaised),
                        border: `1px solid ${c(SURFACE.border)}`,
                        boxShadow: c(SHADOW.popover),
                        borderRadius: RADIUS.sm,
                        padding: '5px 10px',
                        lineHeight: 1.5,
                    },
                    arrow: {
                        color: c(SURFACE.cardRaised),
                        '&::before': {
                            border: `1px solid ${c(SURFACE.border)}`,
                        },
                    },
                },
            },
            MuiStepLabel: {
                styleOverrides: {
                    label: {
                        fontWeight: 500,
                        color: c(TEXT.muted),
                        '&.Mui-active': {
                            fontWeight: 600,
                            color: c(TEXT.primary),
                        },
                        '&.Mui-completed': { color: c(TEXT.secondary) },
                    },
                },
            },
            MuiStepIcon: {
                styleOverrides: {
                    root: {
                        color: c(SURFACE.stepIdle),
                        '& .MuiStepIcon-text': { fill: c(STEP.idleNumber) },
                        '&.Mui-active': {
                            color: BRAND.orange,
                            '& .MuiStepIcon-text': { fill: STEP.activeNumber },
                        },
                        '&.Mui-completed': { color: BRAND.yellow },
                    },
                    text: { fontSize: '0.6875rem', fontWeight: 600 },
                },
            },
            MuiDivider: {
                styleOverrides: { root: { borderColor: c(SURFACE.border) } },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        fontSize: '0.8125rem',
                        color: c(TEXT.secondary),
                        borderBottom: `1px solid ${c(SURFACE.border)}`,
                    },
                    // The stylesheet's OVERLINE, on table headers.
                    head: {
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: c(TEXT.muted),
                        backgroundColor: c(SURFACE.sunken),
                        borderBottom: `1px solid ${c(SURFACE.border)}`,
                        whiteSpace: 'nowrap',
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundColor: c(SURFACE.card),
                        border: `1px solid ${c(SURFACE.border)}`,
                        borderRadius: RADIUS.lg,
                        boxShadow: c(SHADOW.popover),
                        backgroundImage: 'none',
                        '&.MuiDialog-paperFullScreen': {
                            borderRadius: 0,
                            border: 'none',
                        },
                    },
                },
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        '&:not(.MuiBackdrop-invisible)': {
                            backgroundColor: c(SURFACE.backdrop),
                        },
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: c(SURFACE.card),
                        borderColor: c(SURFACE.border),
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        borderRadius: RADIUS.pill,
                        height: 6,
                        backgroundColor: c(SURFACE.sunken),
                    },
                    bar: { borderRadius: RADIUS.pill },
                },
            },
            MuiBadge: {
                styleOverrides: {
                    badge: {
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        minWidth: 16,
                        height: 16,
                        padding: '0 4px',
                    },
                },
            },
            MuiLink: {
                defaultProps: { underline: 'hover' },
                styleOverrides: { root: { color: c(ACCENT.text) } },
            },
            MuiSwitch: {
                styleOverrides: {
                    track: { backgroundColor: c(CONTROL.trackOff), opacity: 1 },
                },
            },
        },
    })
}

export const lightTheme = createAppTheme('light')
export const darkTheme = createAppTheme('dark')

// The brand default; ThemeModeProvider picks the theme at run time.
export default lightTheme
